import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { map, mergeMap, catchError, withLatestFrom, concatMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';

import { RouterStateUrl } from '@sd-portal/shared/util';

import { State } from '..';
import { CompanyProfileAuditService } from '../../company-profile-audit.service';
import * as actions from './company-profile.actions';

@Injectable()
export class CompanyProfileEffects {
  constructor(
    private readonly companyProfileAuditService: CompanyProfileAuditService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  // company profile skeleton
  loadCompanyProfileAuditSkeleton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCompanyProfileAuditSkeleton),
      mergeMap(() =>
        this.companyProfileAuditService.getCompanyProfileSkeleton().pipe(
          map(payload => actions.CompanyProfileAuditSkeletonLoaded({ payload })),
          catchError(err => of(actions.CompanyProfileAuditSkeletonLoadError(err)))
        )
      )
    )
  );

  // company profile
  loadCompanyProfileAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCompanyProfileAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getCompanyProfileAudits().pipe(
          map(audits =>
            actions.CompanyProfileAuditLoaded({
              companyProfileId: action.companyProfileId,
              audits
            })
          ),
          catchError(err => of(actions.CompanyProfileAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageCompanyProfileAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCompanyProfileAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.companyProfileAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getCompanyProfileAudits(nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.CompanyProfileAuditLoaded({
                companyProfileId: action.companyProfileId,
                audits
              })
            ),
            catchError(err => of(actions.CompanyProfileAuditLoadError(err)))
          );
      })
    )
  );

  // aircraft type rating
  loadAircraftTypeRatingAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAircraftTypeRatingAudit),
      mergeMap(action =>
        this.companyProfileAuditService
          .getAircraftTypeRatingAudits(action.aircraftTypeRatingId)
          .pipe(
            map(audits =>
              actions.AircraftTypeRatingAuditLoaded({
                aircraftTypeRatingId: action.aircraftTypeRatingId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftTypeRatingAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageAircraftTypeRatingAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAircraftTypeRatingAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.aircraftTypeRatingAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getAircraftTypeRatingAudits(
            action.aircraftTypeRatingId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.AircraftTypeRatingAuditLoaded({
                aircraftTypeRatingId: action.aircraftTypeRatingId,
                audits
              })
            ),
            catchError(err => of(actions.AircraftTypeRatingAuditLoadError(err)))
          );
      })
    )
  );

  // company address
  loadCompanyAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCompanyAddressAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getCompanyAddressAudits(action.companyAddressId).pipe(
          map(audits =>
            actions.CompanyAddressAuditLoaded({
              companyAddressId: action.companyAddressId,
              audits
            })
          ),
          catchError(err => of(actions.CompanyAddressAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageCompanyAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCompanyAddressAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.companyAddressAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getCompanyAddressAudits(
            action.companyAddressId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.CompanyAddressAuditLoaded({
                companyAddressId: action.companyAddressId,
                audits
              })
            ),
            catchError(err => of(actions.CompanyAddressAuditLoadError(err)))
          );
      })
    )
  );

  // customer duty
  loadCustomerDutyAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCustomerDutyAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getCustomerDutyAudits(action.customerDutyId).pipe(
          map(audits =>
            actions.CustomerDutyAuditLoaded({
              customerDutyId: action.customerDutyId,
              audits
            })
          ),
          catchError(err => of(actions.CustomerDutyAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageCustomerDutyAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCustomerDutyAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.customerDutyAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getCustomerDutyAudits(action.customerDutyId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.CustomerDutyAuditLoaded({
                customerDutyId: action.customerDutyId,
                audits
              })
            ),
            catchError(err => of(actions.CustomerDutyAuditLoadError(err)))
          );
      })
    )
  );

  // delay type
  loadDelayTypeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadDelayTypeAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getDelayTypeAudits(action.delayTypeId).pipe(
          map(audits => actions.DelayTypeAuditLoaded({ delayTypeId: action.delayTypeId, audits })),
          catchError(err => of(actions.DelayTypeAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageDelayTypeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageDelayTypeAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.delayTypeAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getDelayTypeAudits(action.delayTypeId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.DelayTypeAuditLoaded({ delayTypeId: action.delayTypeId, audits })
            ),
            catchError(err => of(actions.DelayTypeAuditLoadError(err)))
          );
      })
    )
  );

  // department
  loadDepartmentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadDepartmentAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getDepartmentAudits(action.departmentId).pipe(
          map(audits =>
            actions.DepartmentAuditLoaded({ departmentId: action.departmentId, audits })
          ),
          catchError(err => of(actions.DepartmentAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageDepartmentAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageDepartmentAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.departmentAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getDepartmentAudits(action.departmentId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.DepartmentAuditLoaded({ departmentId: action.departmentId, audits })
            ),
            catchError(err => of(actions.DepartmentAuditLoadError(err)))
          );
      })
    )
  );

  // duty time buffer
  loadDutyTimeBufferAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadDutyTimeBufferAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getDutyTimeBufferAudits(action.dutyTimeBufferId).pipe(
          map(audits =>
            actions.DutyTimeBufferAuditLoaded({
              dutyTimeBufferId: action.dutyTimeBufferId,
              audits
            })
          ),
          catchError(err => of(actions.DutyTimeBufferAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageDutyTimeBufferAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageDutyTimeBufferAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.dutyTimeBufferAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getDutyTimeBufferAudits(
            action.dutyTimeBufferId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.DutyTimeBufferAuditLoaded({
                dutyTimeBufferId: action.dutyTimeBufferId,
                audits
              })
            ),
            catchError(err => of(actions.DutyTimeBufferAuditLoadError(err)))
          );
      })
    )
  );

  // expense types
  loadExpenseTypeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadExpenseTypeAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getExpenseTypeAudits(action.expenseTypeId).pipe(
          map(audits =>
            actions.ExpenseTypeAuditLoaded({ expenseTypeId: action.expenseTypeId, audits })
          ),
          catchError(err => of(actions.ExpenseTypeAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageExpenseTypeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageExpenseTypeAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.expenseTypeAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getExpenseTypeAudits(action.expenseTypeId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.ExpenseTypeAuditLoaded({ expenseTypeId: action.expenseTypeId, audits })
            ),
            catchError(err => of(actions.ExpenseTypeAuditLoadError(err)))
          );
      })
    )
  );

  // crew member type
  loadCrewMemberTypeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCrewMemberTypeAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getCrewMemberTypeAudits(action.crewMemberTypeId).pipe(
          map(audits =>
            actions.CrewMemberTypeAuditLoaded({
              crewMemberTypeId: action.crewMemberTypeId,
              audits
            })
          ),
          catchError(err => of(actions.CrewMemberTypeAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageCrewMemberTypeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCrewMemberTypeAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.crewMemberTypeAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getCrewMemberTypeAudits(
            action.crewMemberTypeId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.CrewMemberTypeAuditLoaded({
                crewMemberTypeId: action.crewMemberTypeId,
                audits
              })
            ),
            catchError(err => of(actions.CrewMemberTypeAuditLoadError(err)))
          );
      })
    )
  );

  // payment type
  loadPaymentTypeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPaymentTypeAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getPaymentTypeAudits(action.paymentTypeId).pipe(
          map(audits =>
            actions.PaymentTypeAuditLoaded({ paymentTypeId: action.paymentTypeId, audits })
          ),
          catchError(err => of(actions.PaymentTypeAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePaymentTypeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePaymentTypeAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.paymentTypeAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getPaymentTypeAudits(action.paymentTypeId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.PaymentTypeAuditLoaded({ paymentTypeId: action.paymentTypeId, audits })
            ),
            catchError(err => of(actions.PaymentTypeAuditLoadError(err)))
          );
      })
    )
  );

  // business category
  loadBusinessCategoryAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadBusinessCategoryAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getBusinessCategoryAudits(action.businessCategoryId).pipe(
          map(audits =>
            actions.BusinessCategoryAuditLoaded({
              businessCategoryId: action.businessCategoryId,
              audits
            })
          ),
          catchError(err => of(actions.BusinessCategoryAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageBusinessCategoryAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageBusinessCategoryAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.businessCategoryAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getBusinessCategoryAudits(
            action.businessCategoryId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.BusinessCategoryAuditLoaded({
                businessCategoryId: action.businessCategoryId,
                audits
              })
            ),
            catchError(err => of(actions.BusinessCategoryAuditLoadError(err)))
          );
      })
    )
  );

  // job grade
  loadJobGradeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadJobGradeAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getJobGradeAudits(action.jobGradeId).pipe(
          map(audits => actions.JobGradeAuditLoaded({ jobGradeId: action.jobGradeId, audits })),
          catchError(err => of(actions.JobGradeAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageJobGradeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageJobGradeAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.jobGradeAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getJobGradeAudits(action.jobGradeId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.JobGradeAuditLoaded({ jobGradeId: action.jobGradeId, audits })),
            catchError(err => of(actions.JobGradeAuditLoadError(err)))
          );
      })
    )
  );

  // staff event type
  loadStaffEventTypeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadStaffEventTypeAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getStaffEventTypeAudits(action.staffEventTypeId).pipe(
          map(audits =>
            actions.StaffEventTypeAuditLoaded({
              staffEventTypeId: action.staffEventTypeId,
              audits
            })
          ),
          catchError(err => of(actions.StaffEventTypeAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageStaffEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageStaffEventTypeAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.staffEventTypeAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getStaffEventTypeAudits(
            action.staffEventTypeId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.StaffEventTypeAuditLoaded({
                staffEventTypeId: action.staffEventTypeId,
                audits
              })
            ),
            catchError(err => of(actions.StaffEventTypeAuditLoadError(err)))
          );
      })
    )
  );

  // staff group
  loadStaffGroupAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadStaffGroupAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getStaffGroupAudits(action.staffGroupId).pipe(
          map(audits =>
            actions.StaffGroupAuditLoaded({
              staffGroupId: action.staffGroupId,
              audits
            })
          ),
          catchError(err => of(actions.StaffGroupAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageStaffGroupAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageStaffGroupAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.staffGroupAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getStaffGroupAudits(action.staffGroupId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.StaffGroupAuditLoaded({
                staffGroupId: action.staffGroupId,
                audits
              })
            ),
            catchError(err => of(actions.StaffGroupAuditLoadError(err)))
          );
      })
    )
  );
  // duty time allowance
  loadDutyTimeAllowanceAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadDutyTimeAllowanceAudit),
      mergeMap(action =>
        this.companyProfileAuditService.getDutyTimeAllowanceAudits(action.dutyTimeAllowanceId).pipe(
          map(audits =>
            actions.DutyTimeAllowanceAuditLoaded({
              dutyTimeAllowanceId: action.dutyTimeAllowanceId,
              audits
            })
          ),
          catchError(err => of(actions.DutyTimeAllowanceAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageDutyTimeAllowanceAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageDutyTimeAllowanceAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.companyProfile.dutyTimeAllowanceAudit.lastAuditLogId;
        }
        return this.companyProfileAuditService
          .getDutyTimeAllowanceAudits(
            action.dutyTimeAllowanceId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.DutyTimeAllowanceAuditLoaded({
                dutyTimeAllowanceId: action.dutyTimeAllowanceId,
                audits
              })
            ),
            catchError(err => of(actions.DutyTimeAllowanceAuditLoadError(err)))
          );
      })
    )
  );
}
